import React, { useEffect, useState } from 'react';
import Navbar from '../components/storefront/Navbar';
import Footer from '../components/storefront/Footer';
import { useStore } from '../context/StoreContext';
import { useNavigate } from 'react-router-dom';
import { FiTrash2, FiPlus, FiMinus, FiShoppingBag, FiArrowLeft, FiClock } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Cart = () => {
  const { cart, updateCartQuantity, removeFromCart, clearCart, isShopComingSoon } = useStore();
  const navigate = useNavigate();
  const [comingSoon, setComingSoon] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setComingSoon(isShopComingSoon());
  }, []);

  const subtotal = cart.reduce((acc, item) => acc + item.price * item.quantity, 0);

  if (cart.length === 0) {
    return (
      <div className="bg-brand-sand min-h-screen pt-24 font-sans">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <div className="w-24 h-24 bg-brand-gold/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <SafeIcon icon={FiShoppingBag} className="w-12 h-12 text-brand-gold" />
          </div>
          <h1 className="text-3xl font-serif text-brand-darkSage mb-4">Your Cart is Empty</h1>
          <p className="text-brand-lightText mb-8">Looks like you haven't added anything yet.</p>
          <button 
            onClick={() => navigate('/shop')}
            className="bg-brand-darkSage text-white px-8 py-4 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-brand-gold transition-all"
          >
            Start Shopping
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-brand-sand min-h-screen pt-24 font-sans">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-serif text-brand-darkSage mb-10">Shopping Cart</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {cart.map(item => (
              <div key={item.id} className="bg-white/70 backdrop-blur-sm p-5 rounded-2xl border border-white shadow-sm flex items-center gap-5">
                <div className="w-24 h-24 bg-brand-sand/50 rounded-xl flex items-center justify-center p-3 flex-shrink-0">
                  <img src={item.image} alt={item.name} className="w-full h-full object-contain" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-brand-gold uppercase tracking-widest font-semibold">{item.category}</p>
                  <h3 className="font-serif text-lg text-brand-darkSage font-bold truncate">{item.name}</h3>
                  <p className="text-brand-darkSage font-bold mt-1">Rs. {item.price.toFixed(2)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => updateCartQuantity(item.id, item.quantity - 1)} className="w-8 h-8 rounded-full bg-brand-sand flex items-center justify-center hover:bg-brand-gold/20 transition-colors">
                    <SafeIcon icon={FiMinus} size={14} />
                  </button>
                  <span className="font-bold text-brand-darkSage w-6 text-center">{item.quantity}</span>
                  <button onClick={() => updateCartQuantity(item.id, item.quantity + 1)} className="w-8 h-8 rounded-full bg-brand-sand flex items-center justify-center hover:bg-brand-gold/20 transition-colors">
                    <SafeIcon icon={FiPlus} size={14} />
                  </button>
                </div>
                <button onClick={() => removeFromCart(item.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                  <SafeIcon icon={FiTrash2} size={18} />
                </button>
              </div>
            ))}
          </div>

          <div className="bg-white/80 backdrop-blur-xl p-8 rounded-3xl border border-white shadow-lg h-fit sticky top-28">
            <h2 className="font-serif text-xl text-brand-darkSage mb-6">Order Summary</h2>
            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-brand-lightText">Subtotal</span>
                <span className="font-bold text-brand-darkSage">Rs. {subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-brand-lightText">Shipping</span>
                <span className="text-brand-lightText italic">Calculated at checkout</span>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-4 mb-6">
              <div className="flex justify-between">
                <span className="font-bold text-brand-darkSage">Estimated Total</span>
                <span className="text-2xl font-serif font-bold text-brand-darkSage">Rs. {subtotal.toFixed(2)}</span>
              </div>
            </div>

            {comingSoon ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                <SafeIcon icon={FiClock} className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-sm font-bold text-amber-800 mb-1">Shop Coming Soon</p>
                <p className="text-xs text-amber-600">We're not accepting orders yet. Stay tuned!</p>
              </div>
            ) : (
              <button 
                onClick={() => navigate('/checkout')}
                className="w-full bg-brand-darkSage text-white py-4 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-brand-gold transition-all"
              >
                Proceed to Checkout
              </button>
            )}

            <button 
              onClick={() => navigate('/shop')}
              className="w-full mt-4 text-brand-darkSage/70 hover:text-brand-gold text-xs font-bold uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiArrowLeft} size={14} /> Continue Shopping
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Cart;